import { useEffect, useState } from 'react';
import { LOCALE_NAMES } from '@zt/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Textarea,
  type Tone,
  buttonStyles,
} from '@zt/ui';
import { api, IdempotencyKey, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { GeliverOffersView, GeliverOrderInfo, OrderDetail, OrderPage } from '../lib/types';
import { ConfirmButton } from '../components/Confirm';
import { InvoiceActions, invoiceTone } from './invoices';
import { OrderBulkBar, OrderEditActions, OrderFormDialog } from './order-tools';
import { PaymentLinksBlock } from './payment-links';
import { AuthorizationBox } from './preauth';
import { paymentTone } from './payments';
import { CARRIERS } from './shipping';
import { CreditOverrideField, creditOverrideBody } from '../components/CreditOverride';
import { PurchaseOrderSection } from '../components/OrderPurchaseOrder';
import { convertCross, money as moneyOf } from '@zt/money';
import { useBulletinDate, usePairLabel } from './fx';

export const orderTone: Record<string, Tone> = {
  draft: 'neutral',
  awaiting_payment: 'amber',
  paid: 'pine',
  processing: 'pine',
  shipped: 'slate',
  delivered: 'moss',
  completed: 'moss',
  cancelled: 'rose',
};

export function OrdersPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // Toplu işlem seçimi (F5B-12): süzgeç değişince seçim temizlenir.
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => {
    setCursor(null);
    setSelected([]);
  }, [status, q]);
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  if (cursor) params.set('cursor', cursor);
  const list = useQuery({
    queryKey: ['orders', params.toString()],
    queryFn: () => api.get<OrderPage>(`/v1/admin/orders?${params.toString()}`),
  });
  const pageIds = list.data?.items.map((o) => o.id) ?? [];
  return (
    <PageFrame
      helpKey="page.orders"
      actions={
        can('orders.order.write') && (
          <div className="flex items-center gap-1">
            <Button variant="primary" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.orders.createManual')}
            </Button>
            <HelpTip k="orders.manual_create" />
          </div>
        )
      }
    >
      <p className="rounded-sm border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
        {t('admin.orders.paymentNote')}
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
        <Field label={t('admin.orders.search')}>
          {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
        </Field>
        <Field label={t('admin.orders.status')}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {Object.keys(orderTone).map((s) => (
                <option key={s} value={s}>
                  {t(`admin.orders.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {selected.length > 0 && <OrderBulkBar ids={selected} onClear={() => setSelected([])} />}
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('admin.orders.empty')} description={t('admin.orders.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    className="accent-[var(--zt-pine)]"
                    aria-label={t('admin.orderTools.selectAllPage')}
                    checked={pageIds.length > 0 && pageIds.every((id) => selected.includes(id))}
                    onChange={(e) =>
                      setSelected((cur) =>
                        e.target.checked
                          ? [...new Set([...cur, ...pageIds])].slice(0, 100)
                          : cur.filter((x) => !pageIds.includes(x)),
                      )
                    }
                  />
                </th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.orders.number')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.orders.customer')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.orders.channel')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.orders.status')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.orders.total')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((o) => (
                <tr key={o.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="accent-[var(--zt-pine)]"
                      aria-label={t('admin.orderTools.selectRow', { number: o.number })}
                      checked={selected.includes(o.id)}
                      onChange={(e) =>
                        setSelected((cur) =>
                          e.target.checked
                            ? [...new Set([...cur, o.id])].slice(0, 100)
                            : cur.filter((x) => x !== o.id),
                        )
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to="/siparisler/$id"
                      params={{ id: o.id }}
                      className="font-medium text-pine hover:underline"
                    >
                      {o.number}
                    </Link>
                    <p className="text-ink-3">{formatDate(o.placedAt ?? o.createdAt, true)}</p>
                    {o.poNumber && (
                      <p className="text-ink-3">{t('admin.orders.po.listLabel', { number: o.poNumber })}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {o.customerName ?? '—'}
                    {o.companyName && <p className="text-ink-3">{o.companyName}</p>}
                  </td>
                  <td className="px-3 py-2">{t(`admin.orders.channels.${o.channel}`)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={orderTone[o.status]}>{t(`admin.orders.statuses.${o.status}`)}</Badge>
                    {o.fulfillmentStatus === 'partial' && (
                      <Badge tone="amber" className="ms-1">
                        {t('admin.orders.partiallyShipped')}
                      </Badge>
                    )}
                    {o.ledgerSettled && (
                      <Badge tone="moss" className="ms-1">
                        {t('admin.orders.ledgerSettled')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(o.totalMinor, o.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data?.nextCursor && (
        <Button onClick={() => setCursor(list.data!.nextCursor)}>{t('common.more')}</Button>
      )}
      {open && <OrderFormDialog onClose={() => setOpen(false)} />}
    </PageFrame>
  );
}

export function OrderDetailPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { id } = useParams({ from: '/app/siparisler/$id' });
  const o = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<OrderDetail>(`/v1/admin/orders/${id}`),
  });
  const [to, setTo] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const change = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${id}/status`,
        { to, note: note || undefined, ...creditOverrideBody(creditReason) },
        {
          idempotencyKey: newIdempotencyKey(),
          ifMatch: o.data?.version,
        },
      ),
    {
      invalidate: [['order', id], ['orders']],
      success: t('common.saved'),
      onSuccess: () => {
        setTo(null);
        setNote('');
        setCreditReason('');
      },
      silentFieldErrors: true,
    },
  );
  const addNote = useApiMutation(() => api.post(`/v1/admin/orders/${id}/notes`, { message }), {
    invalidate: [['order', id]],
    success: t('common.saved'),
    onSuccess: () => setMessage(''),
  });
  if (o.isLoading || !o.data) return <Spinner label={t('common.loading')} />;
  const d = o.data;
  const promotionSum =
    (d.promotions ?? []).reduce((a, p) => a + p.amountMinor, 0) + (d.loyalty?.discountMinor ?? 0);
  const freeShipping = (d.promotions ?? []).find((p) => p.shippingDiscountMinor > 0);
  // "Kargoda" durumuna gönderi oluşturarak geçilir (takip numarası ve müşteriye e-posta).
  const transitions = d.allowedTransitions.filter((x) => x !== 'shipped');
  const addr = (a: Record<string, unknown> | null) =>
    !a
      ? '—'
      : a.masked
        ? `${a.district ?? ''} ${a.city ?? ''} (${t('admin.customers.masked')})`
        : [a.fullName, a.phone, a.line1, a.line2, a.district, a.city, a.postalCode]
            .filter(Boolean)
            .join(', ');
  return (
    <PageFrame
      helpKey="page.order_detail"
      title={t('admin.orders.detailTitle', { number: d.number })}
      actions={
        <span className="inline-flex flex-wrap items-center gap-2">
          {can('customers.pii.read') && d.status !== 'cancelled' && (
            <span className="inline-flex items-center gap-1">
              <Link
                to="/siparisler/$id/proforma"
                params={{ id: d.id }}
                className={buttonStyles({ variant: 'secondary' })}
              >
                {t('admin.proforma.open')}
              </Link>
              <HelpTip k="orders.proforma" />
            </span>
          )}
          <Link to="/siparisler" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.orders.backToList')}
          </Link>
        </span>
      }
    >
      <div className="flex flex-wrap gap-2">
        <Badge tone={orderTone[d.status]}>{t(`admin.orders.statuses.${d.status}`)}</Badge>
        {d.fulfillmentStatus === 'partial' && (
          <Badge tone="amber">{t('admin.orders.partiallyShipped')}</Badge>
        )}
        <Badge tone="neutral">{t(`admin.orders.payment.${d.paymentStatus}`)}</Badge>
        {d.ledgerSettled && (
          <span className="inline-flex items-center gap-1">
            <Badge tone="moss">{t('admin.orders.ledgerSettled')}</Badge>
            <HelpTip k="orders.ledger_settled" />
          </span>
        )}
        <Badge tone="neutral">{t(`admin.orders.channels.${d.channel}`)}</Badge>
        {d.taxExemption === 'export' && (
          <span className="inline-flex items-center gap-1">
            <Badge tone="pine">{t('admin.orders.exportExempt')}</Badge>
            <HelpTip k="orders.tax_exemption" />
          </span>
        )}
      </div>
      <Section title={t('admin.orders.lines')}>
        <OrderEditActions order={d} />
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.products.product')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.orders.qty')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.orders.unitPrice')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.orders.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    {l.title} <span className="text-ink-3">{l.sku}</span>
                    {l.lotAllocation?.some((a) => a.lotId) && (
                      <p className="text-xs text-ink-3">{t('admin.orders.lotAllocated')}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end tabular">
                    {l.quantity}
                    {l.cancelledQuantity > 0 && (
                      <span className="block text-xs text-amber">
                        {t('admin.orders.cancelledQty', { count: l.cancelledQuantity })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(l.unitPriceMinor, d.currency)}</td>
                  <td className="px-3 py-2 text-end tabular">{money(l.lineTotalMinor, d.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ms-auto mt-3 max-w-xs">
          <KeyValue
            items={[
              { label: t('admin.orders.subtotal'), value: money(d.subtotalMinor, d.currency) },
              // Otomatik kampanyalar (F6-11) ayrı satırda; kalan indirim kupon veya elle indirimdir.
              ...(d.promotions ?? [])
                .filter((p) => p.amountMinor > 0)
                .map((p) => ({
                  label: t('admin.orders.promotion', { label: p.label }),
                  value: `−${money(p.amountMinor, d.currency)}`,
                })),
              // Sadakat seviyesi indirimi (F6-13).
              ...(d.loyalty?.discountMinor
                ? [
                    {
                      label: t('admin.orders.loyaltyTier', {
                        tier: d.loyalty.tierName,
                        percent: bpsLabel(d.loyalty.discountBps),
                      }),
                      value: `−${money(d.loyalty.discountMinor, d.currency)}`,
                    },
                  ]
                : []),
              ...(d.discountMinor - promotionSum > 0
                ? [
                    {
                      label: t('admin.orders.discount', { code: d.discountCode ?? '' }),
                      value: `−${money(d.discountMinor - promotionSum, d.currency)}`,
                    },
                  ]
                : []),
              { label: t('admin.orders.tax'), value: money(d.taxMinor, d.currency) },
              {
                label: t('admin.orders.shipping'),
                value: freeShipping
                  ? t('admin.orders.shippingPromotion', { label: freeShipping.label })
                  : d.loyalty?.shippingDiscountMinor
                    ? t('admin.orders.shippingPromotion', { label: d.loyalty.tierName })
                    : money(d.shippingMinor, d.currency),
              },
              ...(d.codFeeMinor
                ? [{ label: t('admin.orders.codFee'), value: money(d.codFeeMinor, d.currency) }]
                : []),
              { label: t('admin.orders.total'), value: <strong>{money(d.totalMinor, d.currency)}</strong> },
              ...(d.cancelledItemsMinor
                ? [
                    {
                      label: t('admin.orders.cancelledItems'),
                      value: `−${money(d.cancelledItemsMinor, d.currency)}`,
                    },
                  ]
                : []),
            ]}
          />
          {d.fx && <OrderFxNote fx={d.fx} totalMinor={d.totalMinor} />}
        </div>
      </Section>
      <Section title={t('admin.orders.customerInfo')}>
        <KeyValue
          items={[
            {
              label: t('admin.orders.customer'),
              value: d.customerId ? (
                <Link
                  to="/musteriler/$id"
                  params={{ id: d.customerId }}
                  className="text-pine hover:underline"
                >
                  {d.customerName}
                </Link>
              ) : (
                '—'
              ),
            },
            { label: t('admin.dealers.legalName'), value: d.companyName ?? '—' },
            ...(d.companyId ? [{ label: t('admin.sales.rep'), value: d.salesRepName ?? '—' }] : []),
            { label: t('admin.customers.email'), value: d.email ?? '—' },
            { label: t('admin.customers.phone'), value: d.phone ?? '—' },
            {
              label: t('admin.orders.paymentMethod'),
              value: d.paymentMethod ? t(`admin.orders.paymentMethods.${d.paymentMethod}`) : '—',
            },
            { label: t('admin.orders.shippingMethod'), value: d.shippingMethodName ?? '—' },
            { label: t('admin.orders.shippingAddress'), value: addr(d.shippingAddress) },
            { label: t('admin.orders.billingAddress'), value: addr(d.billingAddress) },
            { label: t('admin.orders.customerNote'), value: d.note ?? '—' },
          ]}
        />
      </Section>
      {(d.companyId || d.poNumber || d.poDocument || d.companyApproval) && <PurchaseOrderSection order={d} />}
      <PaymentsSection order={d} />
      {d.returns.length > 0 && <ReturnsSection order={d} />}
      <ShipmentsSection order={d} />
      <InvoicesSection order={d} />
      {d.acceptances.length > 0 && <ContractsSection order={d} />}
      {can('orders.order.write') && transitions.length > 0 && (
        <Section title={t('admin.orders.changeStatus')} action={<HelpTip k="orders.status_change" />}>
          <div className="flex flex-wrap gap-2">
            {transitions.map((s) => (
              <Button key={s} variant={s === 'cancelled' ? 'danger' : 'secondary'} onClick={() => setTo(s)}>
                {t(`admin.orders.actions.${s}`)}
              </Button>
            ))}
          </div>
        </Section>
      )}
      <Section title={t('admin.orders.timeline')}>
        <ol className="space-y-2 border-s border-line ps-4 text-sm">
          {d.events.map((e) => (
            <li key={e.id}>
              <span className="text-ink-3">{formatDate(e.createdAt, true)}</span> ·{' '}
              {e.type === 'note'
                ? t('admin.orders.noteEvent')
                : e.toStatus
                  ? t(`admin.orders.statuses.${e.toStatus}`)
                  : tOr(t, `admin.orders.eventTypes.${e.type}`, e.type)}
              {e.message && (
                <p className="text-ink-2">
                  {e.type === 'return_requested'
                    ? tOr(t, `admin.returns.kinds.${e.message}`, e.message)
                    : eventMessage(t, e.message)}
                </p>
              )}
            </li>
          ))}
        </ol>
        {can('orders.order.write') && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <Field
              label={t('admin.orders.internalNote')}
              className="min-w-64 flex-1"
              help={<HelpTip k="orders.notes" />}
            >
              {(p) => <Input {...p} value={message} onChange={(e) => setMessage(e.target.value)} />}
            </Field>
            <Button
              loading={addNote.isPending}
              disabled={!message.trim()}
              onClick={() => addNote.mutate(undefined)}
            >
              {t('admin.orders.addNote')}
            </Button>
          </div>
        )}
      </Section>
      {to && (
        <Dialog
          open
          onOpenChange={(v) => !v && setTo(null)}
          title={t(`admin.orders.actions.${to}`)}
          description={t(`admin.orders.actionHints.${to}`)}
          closeLabel={t('common.close')}
          footer={
            <>
              <Button onClick={() => setTo(null)}>{t('common.cancel')}</Button>
              <Button
                variant={to === 'cancelled' ? 'danger' : 'primary'}
                loading={change.isPending}
                onClick={() => change.mutate(undefined)}
              >
                {t('admin.confirm.yes')}
              </Button>
            </>
          }
        >
          <Field
            label={t('admin.orders.statusNote')}
            optionalLabel={t('common.optional')}
            error={
              fieldError(change.error, 'note') ??
              (change.error && !change.error.errors.length ? change.error.title : undefined)
            }
          >
            {(p) => (
              <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </Field>
          <CreditOverrideField error={change.error} value={creditReason} onChange={setCreditReason} />
        </Dialog>
      )}
    </PageFrame>
  );
}

// ------------------------------------------------------------------ Ödeme, iade, gönderi, fatura, sözleşmeler

const REFUND_REASONS = [
  'customer_request',
  'withdrawal',
  'damaged',
  'out_of_stock',
  'duplicate',
  'other',
] as const;
const SHIPMENT_STATUSES = [
  'in_transit',
  'out_for_delivery',
  'delivered',
  'returned',
  'exception',
  'cancelled',
] as const;

function PaymentsSection({ order: d }: { order: OrderDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [capture, setCapture] = useState(false);
  const [refund, setRefund] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const inv = [['order', d.id], ['orders'], ['payments']];
  return (
    <Section
      title={t('admin.orders.paymentsTitle')}
      action={
        <div className="flex flex-wrap items-center gap-1">
          {d.canCapture && can('payments.payment.capture') && (
            <>
              <Button size="sm" variant="primary" onClick={() => setCapture(true)}>
                {t('admin.orders.capture')}
              </Button>
              <HelpTip k="orders.capture" />
            </>
          )}
          {d.canRefund && can('orders.refund.approve') && (
            <>
              <Button size="sm" onClick={() => setRefund(true)}>
                {t('admin.orders.refund')}
              </Button>
              <HelpTip k="orders.refund" />
            </>
          )}
        </div>
      }
    >
      <AuthorizationBox order={d} />
      {d.payments.some((p) => p.status === 'captured' && p.environment === 'sandbox') && (
        <p className="mb-3 rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm text-ink">
          {t('admin.orders.testPaymentWarning')}
        </p>
      )}
      {!d.payments.length ? (
        <p className="text-sm text-ink-2">{t('admin.orders.noPayments')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
          {d.payments.map((p) => (
            <li key={p.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {t(`admin.payments.providers.${p.provider}`)}
                  {p.giftCard &&
                    (can('payments.giftcard.read') ? (
                      <>
                        {' '}
                        <Link
                          to="/hediye-kartlari/$id"
                          params={{ id: p.giftCard.id }}
                          className="font-normal text-pine underline underline-offset-2 hover:no-underline"
                        >
                          {t('admin.giftCards.cardTitle', { last4: p.giftCard.last4 })}
                        </Link>
                      </>
                    ) : (
                      <span className="font-normal text-ink-3">
                        {' '}
                        {t('admin.giftCards.cardTitle', { last4: p.giftCard.last4 })}
                      </span>
                    ))}
                  {p.loyaltyPoints !== null && (
                    <span className="font-normal text-ink-3">
                      {' '}
                      {t('admin.orders.loyaltyPoints', { points: p.loyaltyPoints })}
                    </span>
                  )}
                  {p.environment && (
                    <span className="font-normal text-ink-3">
                      {' '}
                      ·{' '}
                      {p.environment === 'live'
                        ? t('admin.payments.envLive')
                        : t('admin.payments.envSandbox')}
                    </span>
                  )}
                </p>
                <p className="text-ink-3">
                  {formatDate(p.capturedAt ?? p.createdAt, true)} · {p.reference}
                  {p.cardSummary?.lastFour
                    ? ` · ${p.cardSummary.association ?? ''} ···· ${p.cardSummary.lastFour}`
                    : ''}
                  {p.installmentCount && p.installmentCount > 1
                    ? ` · ${t('admin.orders.installments', { count: p.installmentCount })}`
                    : ''}
                </p>
                {p.status === 'requires_action' && p.failureCode && (
                  <p className="text-amber">{tOr(t, `errors.${p.failureCode}`, p.failureCode)}</p>
                )}
                {p.failureMessage && <p className="text-rose">{p.failureMessage}</p>}
                {p.note && <p className="text-ink-2">{p.note}</p>}
              </div>
              <span className="tabular">{money(p.amountMinor, p.currency)}</span>
              {p.refundedMinor > 0 && (
                <span className="tabular text-ink-3">
                  {t('admin.orders.refundedAmount', { amount: money(p.refundedMinor, p.currency) })}
                </span>
              )}
              <Badge tone={paymentTone[p.status]}>{t(`admin.payments.statuses.${p.status}`)}</Badge>
            </li>
          ))}
        </ul>
      )}
      {d.refundDueMinor > 0 && (
        <p
          className="mt-3 rounded-sm border border-amber/40 bg-amber-tint px-3 py-2 text-sm text-ink"
          role="status"
        >
          {t('admin.orders.refundDue', { amount: money(d.refundDueMinor, d.currency) })}
        </p>
      )}
      {d.refunds.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium">{t('admin.orders.refunds')}</p>
          <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
            {d.refunds.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p>
                    {t(`admin.orders.refundReasons.${r.reason}`)}
                    {r.method === 'store_credit' && (
                      <span className="text-ink-3"> · {t('admin.orders.refundToStoreCredit')}</span>
                    )}
                    {r.restock && <span className="text-ink-3"> · {t('admin.orders.restocked')}</span>}
                  </p>
                  {r.note && <p className="text-ink-2">{r.note}</p>}
                  {r.error && <p className="text-rose">{r.error}</p>}
                </div>
                <span className="tabular">{money(r.amountMinor, r.currency)}</span>
                <Badge tone={r.status === 'succeeded' ? 'moss' : r.status === 'failed' ? 'rose' : 'amber'}>
                  {t(`admin.orders.refundStatuses.${r.status}`)}
                </Badge>
                {r.status === 'pending' &&
                  can('orders.refund.approve') &&
                  Date.now() - new Date(r.createdAt).getTime() > STUCK_REFUND_MS && (
                    <span className="flex items-center gap-1">
                      <Button size="sm" onClick={() => setResolving(r.id)}>
                        {t('admin.orders.resolveRefund')}
                      </Button>
                      <HelpTip k="orders.resolve_refund" />
                    </span>
                  )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <PaymentLinksBlock order={d} />
      {capture && <CaptureDialog order={d} invalidate={inv} onClose={() => setCapture(false)} />}
      {refund && <RefundDialog order={d} invalidate={inv} onClose={() => setRefund(false)} />}
      {resolving && (
        <ResolveRefundDialog refundId={resolving} invalidate={inv} onClose={() => setResolving(null)} />
      )}
    </Section>
  );
}

type ReturnDecision = 'approved' | 'rejected' | 'completed' | 'cancelled';
const RETURN_NEXT: Record<string, ReturnDecision[]> = {
  requested: ['approved', 'rejected', 'cancelled'],
  approved: ['completed', 'cancelled'],
};
const returnTone: Record<string, 'amber' | 'moss' | 'rose' | 'neutral'> = {
  requested: 'amber',
  approved: 'moss',
  rejected: 'rose',
  completed: 'moss',
  cancelled: 'neutral',
};

/**
 * Müşterinin iade/cayma ve değişim talepleri: onay (gönderim talimatıyla), gerekçeli ret, tamamlandı, iptal.
 * Değişim (F5B-16) "Değişimi kargoya ver" ile tamamlanır.
 */
function ReturnsSection({ order: d }: { order: OrderDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [decide, setDecide] = useState<{
    id: string;
    version: number;
    decision: ReturnDecision;
    exchange: boolean;
  } | null>(null);
  const [shipFor, setShipFor] = useState<(typeof d.returns)[number] | null>(null);
  return (
    <Section title={t('admin.returns.title')} action={<HelpTip k="orders.returns" />}>
      <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
        {d.returns.map((r) => (
          <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {t(`admin.returns.kinds.${r.kind}`)} · {t(`admin.returns.resolutions.${r.resolution}`)}
              </p>
              <p className="text-ink-3">{formatDate(r.createdAt, true)}</p>
              <p>{r.lines.map((l) => `${l.quantity} × ${l.title}`).join(', ')}</p>
              {r.exchangeItems && (
                <p>
                  {t('admin.returns.exchangeTo', {
                    items: r.exchangeItems.map((i) => `${i.quantity} × ${i.title}`).join(', '),
                  })}
                </p>
              )}
              {r.exchangeShippedAt && (
                <p className="text-ink-2">
                  {t('admin.returns.exchangeShipped', {
                    carrier: t(`admin.shipping.carriers.${r.exchangeCarrier}`),
                    tracking: r.exchangeTrackingNumber ?? '',
                  })}
                  {r.exchangeTrackingUrl && (
                    <>
                      {' · '}
                      <a
                        href={r.exchangeTrackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pine underline underline-offset-2"
                      >
                        {t('admin.orders.trackingLink')}
                      </a>
                    </>
                  )}{' '}
                  {t(r.restocked ? 'admin.returns.restockedYes' : 'admin.returns.restockedNo')}
                </p>
              )}
              {r.customerNote && (
                <p className="text-ink-2">
                  {t('admin.returns.customerNote')}: {r.customerNote}
                </p>
              )}
              {r.decisionNote && (
                <p className="text-ink-2">
                  {t('admin.returns.decisionNote')}: {r.decisionNote}
                </p>
              )}
            </div>
            <Badge tone={returnTone[r.status]}>{t(`admin.returns.statuses.${r.status}`)}</Badge>
            {r.resolution === 'exchange' && r.status === 'approved' && can('shipping.shipment.write') && (
              <span className="flex items-center gap-1">
                <Button size="sm" variant="primary" onClick={() => setShipFor(r)}>
                  {t('admin.returns.shipExchange')}
                </Button>
                <HelpTip k="orders.exchange_ship" />
              </span>
            )}
            {can('orders.order.write') &&
              (RETURN_NEXT[r.status] ?? [])
                // Değişim "Tamamlandı" ile değil, "Değişimi kargoya ver" ile kapanır.
                .filter((next) => !(r.resolution === 'exchange' && next === 'completed'))
                .map((next) => (
                  <Button
                    key={next}
                    size="sm"
                    variant={next === 'approved' || next === 'completed' ? 'primary' : 'ghost'}
                    onClick={() =>
                      setDecide({
                        id: r.id,
                        version: r.version,
                        decision: next,
                        exchange: r.resolution === 'exchange',
                      })
                    }
                  >
                    {t(`admin.returns.actions.${next}`)}
                  </Button>
                ))}
          </li>
        ))}
      </ul>
      {decide && <ReturnDecisionDialog orderId={d.id} {...decide} onClose={() => setDecide(null)} />}
      {shipFor && <ExchangeShipDialog orderId={d.id} request={shipFor} onClose={() => setShipFor(null)} />}
    </Section>
  );
}

function ReturnDecisionDialog({
  orderId,
  id,
  version,
  decision,
  exchange,
  onClose,
}: {
  orderId: string;
  id: string;
  version: number;
  decision: ReturnDecision;
  exchange: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  const m = useApiMutation(
    () =>
      api.post(`/v1/admin/returns/${id}/decision`, { decision, note: note || null }, { ifMatch: version }),
    {
      invalidate: [
        ['order', orderId],
        ['returns', 'requested'],
      ],
      success: t(`admin.returns.done.${decision}`),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const required = decision === 'rejected' || decision === 'cancelled';
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`admin.returns.actions.${decision}`)}
      description={
        exchange && decision !== 'completed'
          ? t(`admin.returns.exchangeLeads.${decision}`)
          : t(`admin.returns.leads.${decision}`)
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={required ? 'danger' : 'primary'}
            loading={m.isPending}
            disabled={required && !note.trim()}
            onClick={() => m.mutate(undefined)}
          >
            {t(`admin.returns.actions.${decision}`)}
          </Button>
        </>
      }
    >
      <Field
        label={t(`admin.returns.noteLabels.${decision}`)}
        required={required}
        optionalLabel={required ? undefined : t('common.optional')}
        help={<HelpTip k="orders.return_note" />}
        error={fieldError(m.error, 'note') ?? (m.error && !m.error.errors.length ? m.error.title : undefined)}
      >
        {(p) => (
          <Textarea {...p} className="min-h-20" value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

/** Değişim (F5B-16): iade edilen ürün geldi; yeni ürün takip numarasıyla gönderilir, talep tamamlanır. */
function ExchangeShipDialog({
  orderId,
  request: r,
  onClose,
}: {
  orderId: string;
  request: OrderDetail['returns'][number];
  onClose: () => void;
}) {
  const t = useT();
  const [f, setF] = useState({
    carrier: 'yurtici',
    trackingNumber: '',
    trackingUrl: '',
    restock: r.kind !== 'defective',
  });
  const [idem] = useState(() => new IdempotencyKey());
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/returns/${r.id}/exchange-shipment`,
        {
          carrier: f.carrier,
          trackingNumber: f.trackingNumber.trim(),
          trackingUrl: f.trackingUrl.trim() || null,
          restock: f.restock,
        },
        { idempotencyKey: idem, ifMatch: r.version },
      ),
    {
      invalidate: [['order', orderId], ['returns', 'requested'], ['orders']],
      success: t('admin.returns.exchangeShippedDone'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.returns.shipExchange')}
      description={t('admin.returns.shipExchangeLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('admin.returns.shipExchange')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <p className="text-sm sm:col-span-2">
          {t('admin.returns.exchangeTo', {
            items: (r.exchangeItems ?? []).map((i) => `${i.quantity} × ${i.title}`).join(', '),
          })}
        </p>
        <Field label={t('admin.shipping.carrier')}>
          {(p) => (
            <Select {...p} value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}>
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.shipping.carriers.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.orders.trackingNumber')}
          required
          error={fieldError(m.error, 'trackingNumber')}
        >
          {(p) => (
            <Input
              {...p}
              className="tabular"
              value={f.trackingNumber}
              onChange={(e) => setF({ ...f, trackingNumber: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.orders.trackingUrl')}
          optionalLabel={t('common.optional')}
          className="sm:col-span-2"
          description={t('admin.orders.trackingUrlHint')}
          error={fieldError(m.error, 'trackingUrl')}
        >
          {(p) => (
            <Input
              {...p}
              type="url"
              placeholder="https://"
              value={f.trackingUrl}
              onChange={(e) => setF({ ...f, trackingUrl: e.target.value })}
            />
          )}
        </Field>
        <label className="flex items-start gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--zt-pine)]"
            checked={f.restock}
            onChange={(e) => setF({ ...f, restock: e.target.checked })}
          />
          <span>
            {t('admin.returns.restock')} <HelpTip k="orders.restock" />
            <span className="block text-ink-3">{t('admin.returns.restockHint')}</span>
          </span>
        </label>
      </div>
      {m.error && !m.error.errors.length && (
        <p role="alert" className="mt-3 text-sm text-rose">
          {m.error.title}
        </p>
      )}
    </Dialog>
  );
}

/** Sunucudaki STUCK_REFUND_MINUTES (10 dk) ile aynı: daha önce düğme gösterilmez. */
const STUCK_REFUND_MS = 10 * 60_000;

/** Zaman çizelgesi iletisi: sistemin yazdığı kodlar (ör. payment_timeout) okunur metne çevrilir. */
function eventMessage(t: (k: string) => string, message: string) {
  if (!/^[a-z_]+$/.test(message)) return message;
  return tOr(t, `admin.orders.eventMessages.${message}`, tOr(t, `errors.${message}`, message));
}

/** Çeviri yoksa (anahtarın kendisi dönerse) yedek metin. */
function tOr(t: (k: string) => string, key: string, fallback: string) {
  const v = t(key);
  return v === key ? fallback : v;
}

function ResolveRefundDialog({
  refundId,
  invalidate,
  onClose,
}: {
  refundId: string;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const [outcome, setOutcome] = useState<'succeeded' | 'failed' | ''>('');
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const m = useApiMutation(
    () => api.post(`/v1/admin/refunds/${refundId}/resolve`, { outcome, note }, { idempotencyKey: idem }),
    { invalidate, success: t('admin.orders.resolveRefundDone'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.resolveRefundTitle')}
      description={t('admin.orders.resolveRefundLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!outcome}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.orders.resolveRefundSave')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.orders.resolveRefundOutcome')}
          required
          help={<HelpTip k="orders.resolve_refund_outcome" />}
          error={fieldError(m.error, 'outcome')}
        >
          {(p) => (
            <Select
              {...p}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as 'succeeded' | 'failed' | '')}
            >
              <option value="">—</option>
              <option value="succeeded">{t('admin.orders.resolveRefundSucceeded')}</option>
              <option value="failed">{t('admin.orders.resolveRefundFailed')}</option>
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.orders.resolveRefundNote')}
          required
          help={<HelpTip k="orders.resolve_refund_note" />}
          error={
            fieldError(m.error, 'note') ?? (m.error && !m.error.errors.length ? m.error.title : undefined)
          }
        >
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function CaptureDialog({
  order: d,
  invalidate,
  onClose,
}: {
  order: OrderDetail;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const money = useMoney();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const m = useApiMutation(
    () =>
      api.post(`/v1/admin/orders/${d.id}/capture`, { note }, { idempotencyKey: idem, ifMatch: d.version }),
    { invalidate, success: t('admin.orders.captured'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.captureTitle', { amount: money(d.totalMinor, d.currency) })}
      description={t('admin.orders.captureLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('admin.orders.capture')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.orders.captureNote')}
        required
        help={<HelpTip k="orders.payment_note" />}
        error={fieldError(m.error, 'note') ?? (m.error && !m.error.errors.length ? m.error.title : undefined)}
      >
        {(p) => (
          <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

const TENDERS: string[] = ['gift_card', 'store_credit', 'loyalty_points'];

function RefundDialog({
  order: d,
  invalidate,
  onClose,
}: {
  order: OrderDetail;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const money = useMoney();
  // Birden çok ödeme (ör. hediye kartı + kart, F6-12): iade edilecek ödeme seçilir; varsayılan önce kart/havale.
  const refundable = d.payments.filter((p) => p.status === 'captured' && p.amountMinor > p.refundedMinor);
  const [paymentId, setPaymentId] = useState(
    () => (refundable.find((p) => !TENDERS.includes(p.provider)) ?? refundable[0])?.id ?? '',
  );
  const selected = refundable.find((p) => p.id === paymentId) ?? null;
  // Kısmi iptalden iade bekleyen tutar varsa önerilir (F5B-27); seçilen ödemede kalandan fazla önerilmez.
  const suggest = (left: number | undefined) =>
    minorToInput(Math.min(d.refundDueMinor || d.refundableMinor, left ?? d.refundableMinor));
  const [amount, setAmount] = useState(() =>
    suggest(selected ? selected.amountMinor - selected.refundedMinor : undefined),
  );
  const [reason, setReason] = useState<(typeof REFUND_REASONS)[number]>(
    d.refundDueMinor ? 'out_of_stock' : 'customer_request',
  );
  const [note, setNote] = useState('');
  const shipped = ['shipped', 'delivered', 'completed'].includes(d.status);
  const [restock, setRestock] = useState(false);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [idem] = useState(() => new IdempotencyKey());
  const tender = !!selected && TENDERS.includes(selected.provider);
  const [toCredit, setToCredit] = useState(false);
  const [consent, setConsent] = useState(false);
  const creditAllowed = !!d.customerId && !tender && reason !== 'withdrawal';
  const asCredit = creditAllowed && toCredit;
  // Sağlayıcı üzerinden iade (iyzico, PayTR, Stripe, PayPal — F7-16); diğerleri elle iade (açıklama zorunlu).
  const card =
    !asCredit && !!selected && ['iyzico', 'paytr', 'stripe', 'paypal', 'sipay'].includes(selected.provider);
  const manual = !asCredit && !tender && !card;
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${d.id}/refunds`,
        {
          amountMinor: toMinor(amount, d.currency) ?? 0,
          reason,
          note: note.trim() || null,
          restock,
          lines: restock
            ? d.lines
                .filter((l) => Number(qty[l.id]) > 0)
                .map((l) => ({ orderLineId: l.id, quantity: Number(qty[l.id]) }))
            : undefined,
          ...(refundable.length > 1 && selected ? { paymentId: selected.id } : {}),
          ...(asCredit ? { toStoreCredit: true, customerConsent: consent } : {}),
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate,
      success: (r: unknown) =>
        (r as { status: string }).status === 'succeeded'
          ? t('admin.orders.refundDone')
          : t('admin.orders.refundFailed'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.orders.refundTitle')}
      description={
        asCredit
          ? t('admin.orders.refundCreditLead')
          : tender
            ? t('admin.orders.refundTenderLead')
            : card
              ? t('admin.orders.refundCardLead')
              : t('admin.orders.refundManualLead')
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('admin.orders.refundConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          {t('admin.orders.refundable', { amount: money(d.refundableMinor, d.currency) })}
        </p>
        {refundable.length > 1 && (
          <Field label={t('admin.orders.refundPayment')} help={<HelpTip k="orders.refund_payment" />}>
            {(p) => (
              <Select
                {...p}
                value={paymentId}
                onChange={(e) => {
                  const next = refundable.find((x) => x.id === e.target.value);
                  setPaymentId(e.target.value);
                  if (next) setAmount(suggest(next.amountMinor - next.refundedMinor));
                }}
              >
                {refundable.map((x) => (
                  <option key={x.id} value={x.id}>
                    {t('admin.orders.refundPaymentOption', {
                      provider: t(`admin.payments.providers.${x.provider}`),
                      amount: money(x.amountMinor - x.refundedMinor, x.currency),
                    })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.orders.refundAmount')} required error={fieldError(m.error, 'amountMinor')}>
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              className="tabular"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('admin.orders.refundReason')}>
          {(p) => (
            <Select {...p} value={reason} onChange={(e) => setReason(e.target.value as typeof reason)}>
              {REFUND_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`admin.orders.refundReasons.${r}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {creditAllowed && (
          <div className="space-y-2 rounded-sm border border-line px-3 py-2.5">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--zt-pine)]"
                checked={toCredit}
                onChange={(e) => {
                  setToCredit(e.target.checked);
                  if (!e.target.checked) setConsent(false);
                }}
              />
              <span>
                {t('admin.orders.refundAsCredit')} <HelpTip k="orders.refund_store_credit" />
              </span>
            </label>
            {toCredit && (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 accent-[var(--zt-pine)]"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  {t('admin.orders.refundCreditConsent')} <HelpTip k="orders.refund_consent" />
                </span>
              </label>
            )}
            {fieldError(m.error, 'customerConsent') && (
              <p role="alert" className="text-[0.8125rem] font-medium text-rose">
                {fieldError(m.error, 'customerConsent')}
              </p>
            )}
          </div>
        )}
        {fieldError(m.error, 'toStoreCredit') && (
          <p role="alert" className="text-[0.8125rem] font-medium text-rose">
            {fieldError(m.error, 'toStoreCredit')}
          </p>
        )}
        <Field
          label={t('admin.orders.refundNote')}
          required={manual}
          optionalLabel={t('common.optional')}
          error={fieldError(m.error, 'note')}
          description={manual ? t('admin.orders.refundNoteHint') : undefined}
        >
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {shipped && (
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--zt-pine)]"
                checked={restock}
                onChange={(e) => setRestock(e.target.checked)}
              />
              <span>
                {t('admin.orders.restock')} <HelpTip k="orders.restock" />
              </span>
            </label>
            {restock && (
              <ul className="space-y-2">
                {d.lines.map((l) => (
                  <li key={l.id} className="grid grid-cols-[1fr_6rem] items-center gap-2 text-sm">
                    <span>
                      {l.title} <span className="text-ink-3">× {l.quantity}</span>
                    </span>
                    <Input
                      aria-label={t('admin.orders.restockQty', { title: l.title })}
                      inputMode="numeric"
                      className="h-9 tabular"
                      value={qty[l.id] ?? ''}
                      onChange={(e) => setQty({ ...qty, [l.id]: e.target.value.replace(/\D/g, '') })}
                    />
                  </li>
                ))}
              </ul>
            )}
            {fieldError(m.error, 'lines') && (
              <p role="alert" className="text-[0.8125rem] font-medium text-rose">
                {fieldError(m.error, 'lines')}
              </p>
            )}
          </div>
        )}
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ShipmentsSection({ order: d }: { order: OrderDetail }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [ship, setShip] = useState(false);
  const [update, setUpdate] = useState<string | null>(null);
  const write = can('shipping.shipment.write');
  const deferred = ['open_account', 'cash_on_delivery'].includes(d.paymentMethod ?? '');
  const canShip = write && d.status === 'processing' && (d.paymentStatus === 'paid' || deferred);
  const inv = [['order', d.id], ['orders']];
  const titleOf = new Map(d.lines.map((l) => [l.id, l.title]));
  const left = (l: OrderDetail['lines'][number]) => l.quantity - l.shippedQuantity - l.cancelledQuantity;
  const waiting = d.lines.filter((l) => left(l) > 0);
  const [cancelItems, setCancelItems] = useState(false);
  const split =
    d.shipments.filter((s) => s.status !== 'cancelled').length > 1 || d.fulfillmentStatus === 'partial';
  // Geliver ile kargo (F7-28): bağlıysa teklif al → etiket satın al.
  const gq = useQuery({
    queryKey: ['geliver-order', d.id],
    queryFn: () => api.get<GeliverOrderInfo>(`/v1/admin/orders/${d.id}/geliver`),
    enabled: canShip,
    retry: false,
  });
  const [geliverOpen, setGeliverOpen] = useState(false);
  const geliverReady = !!gq.data?.connected && gq.data.senderSet && !gq.data.codUnsupported;
  const sync = useApiMutation((id: string) => api.post(`/v1/admin/shipments/${id}/geliver/sync`), {
    invalidate: inv,
    success: t('admin.orders.geliver.synced'),
  });
  const cancelGeliver = useApiMutation(
    (id: string) =>
      api.post(`/v1/admin/shipments/${id}/geliver/cancel`, {}, { idempotencyKey: newIdempotencyKey() }),
    { invalidate: [...inv, ['geliver-order', d.id]], success: t('admin.orders.geliver.cancelled') },
  );
  if (!d.shipments.length && !canShip && d.status !== 'processing') return null;
  return (
    <Section
      title={t('admin.orders.shipmentsTitle')}
      action={
        canShip && (
          <div className="flex flex-wrap items-center gap-1">
            {gq.data?.connected && (
              <>
                <Button size="sm" disabled={!geliverReady} onClick={() => setGeliverOpen(true)}>
                  {t('admin.orders.geliver.ship')}
                </Button>
                <HelpTip k="orders.ship_geliver" />
              </>
            )}
            <Button size="sm" variant="primary" onClick={() => setShip(true)}>
              {t('admin.orders.ship')}
            </Button>
            <HelpTip k="orders.ship" />
          </div>
        )
      }
    >
      {canShip && gq.data?.connected && !geliverReady && (
        <p className="mb-2 text-sm text-ink-2">
          {gq.data.codUnsupported ? (
            t('admin.orders.geliver.codNote')
          ) : (
            <Link to="/kargo" className="text-pine underline underline-offset-2">
              {t('admin.orders.geliver.notReady')}
            </Link>
          )}
        </p>
      )}
      {!d.shipments.length ? (
        <p className="text-sm text-ink-2">
          {d.status === 'processing' && !canShip && !deferred && d.paymentStatus !== 'paid'
            ? t('admin.orders.shipAfterPayment')
            : t('admin.orders.noShipments')}
        </p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
          {d.shipments.map((s) => {
            const gd =
              s.provider === 'geliver'
                ? ((s.providerData ?? {}) as {
                    test?: boolean;
                    amountMinor?: number | null;
                    currency?: string | null;
                    labelUrl?: string | null;
                  })
                : null;
            const open = !['delivered', 'returned', 'cancelled'].includes(s.status);
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {t(`admin.shipping.carriers.${s.carrier}`)}
                    {s.trackingNumber && (
                      <span className="tabular font-normal text-ink-2"> · {s.trackingNumber}</span>
                    )}
                    {gd && (
                      <>
                        {' '}
                        <Badge tone="slate">{t('admin.orders.geliver.badge')}</Badge>
                        {gd.test && (
                          <>
                            {' '}
                            <Badge tone="amber">{t('admin.orders.geliver.testBadge')}</Badge>
                          </>
                        )}
                      </>
                    )}
                  </p>
                  {gd && (
                    <p className="flex flex-wrap items-center gap-x-3 text-ink-2">
                      {typeof gd.amountMinor === 'number' && gd.currency && (
                        <span className="tabular">
                          {t('admin.orders.geliver.price', { amount: money(gd.amountMinor, gd.currency) })}
                        </span>
                      )}
                      {gd.labelUrl && (
                        <span className="flex items-center gap-1">
                          <a
                            href={gd.labelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            // Dokunma hedefi en az 24 px (WCAG 2.2 hedef boyutu).
                            className="inline-flex min-h-6 items-center font-medium text-pine underline underline-offset-2"
                          >
                            {t('admin.orders.geliver.label')}
                          </a>
                          <HelpTip k="orders.geliver_label" />
                        </span>
                      )}
                      {!s.trackingNumber && s.status === 'created' && (
                        <span className="text-ink-3">{t('admin.orders.geliver.awaitingTracking')}</span>
                      )}
                    </p>
                  )}
                  {split && s.lines.length > 0 && (
                    <p className="text-ink-2">
                      {s.lines
                        .map((x) =>
                          t('admin.orders.shipmentItem', {
                            quantity: x.quantity,
                            title: titleOf.get(x.orderLineId) ?? '',
                          }),
                        )
                        .join(', ')}
                    </p>
                  )}
                  <p className="text-ink-3">
                    {s.shippedAt ? formatDate(s.shippedAt, true) : '—'}
                    {s.trackingUrl && (
                      <>
                        {' · '}
                        <a
                          href={s.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pine underline underline-offset-2"
                        >
                          {t('admin.orders.trackingLink')}
                        </a>
                      </>
                    )}
                  </p>
                </div>
                <Badge
                  tone={
                    s.status === 'delivered'
                      ? 'moss'
                      : s.status === 'exception' || s.status === 'returned'
                        ? 'rose'
                        : 'slate'
                  }
                >
                  {t(`admin.orders.shipmentStatuses.${s.status}`)}
                </Badge>
                {gd && write && open && (
                  <span className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={sync.isPending && sync.variables === s.id}
                      onClick={() => sync.mutate(s.id)}
                    >
                      {t('admin.orders.geliver.sync')}
                    </Button>
                    <HelpTip k="orders.geliver_sync" />
                  </span>
                )}
                {gd && write && s.status === 'created' && (
                  <span className="flex items-center gap-1">
                    <ConfirmButton
                      message={t('admin.orders.geliver.cancelConfirm')}
                      onConfirm={() => cancelGeliver.mutateAsync(s.id)}
                    >
                      {t('admin.orders.geliver.cancel')}
                    </ConfirmButton>
                    <HelpTip k="orders.geliver_cancel" />
                  </span>
                )}
                {write && open && (
                  <Button size="sm" variant="ghost" onClick={() => setUpdate(s.id)}>
                    {t('admin.orders.updateShipment')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {d.fulfillmentStatus === 'partial' && waiting.length > 0 && (
        <p className="mt-2 text-sm text-ink-2">
          {t('admin.orders.waitingToShip', {
            items: waiting
              .map((l) => t('admin.orders.shipmentItem', { quantity: left(l), title: l.title }))
              .join(', '),
          })}
        </p>
      )}
      {d.canCancelItems && (
        <div className="mt-2 flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setCancelItems(true)}>
            {t('admin.orders.cancelItems')}
          </Button>
          <HelpTip k="orders.cancel_items" />
        </div>
      )}
      {cancelItems && <CancelItemsDialog order={d} onClose={() => setCancelItems(false)} />}
      {ship && <ShipDialog order={d} invalidate={inv} onClose={() => setShip(false)} />}
      {geliverOpen && gq.data && (
        <GeliverShipDialog
          order={d}
          info={gq.data}
          invalidate={[...inv, ['geliver-order', d.id]]}
          onClose={() => setGeliverOpen(false)}
        />
      )}
      {update && (
        <ShipmentStatusDialog shipmentId={update} invalidate={inv} onClose={() => setUpdate(null)} />
      )}
    </Section>
  );
}

function ShipDialog({
  order: d,
  invalidate,
  onClose,
}: {
  order: OrderDetail;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const [f, setF] = useState({ carrier: 'yurtici', trackingNumber: '', trackingUrl: '', packageCount: '1' });
  const [idem] = useState(() => new IdempotencyKey());
  // Kısmi gönderi (F5B-15): varsayılan, henüz gönderilmemiş adetlerin tamamı; kapıda ödemede bölünemez.
  const rest = (l: OrderDetail['lines'][number]) => l.quantity - l.shippedQuantity - l.cancelledQuantity;
  const pending = d.lines.filter((l) => rest(l) > 0);
  const cod = d.paymentMethod === 'cash_on_delivery';
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(pending.map((l) => [l.id, String(rest(l))])),
  );
  const picked = pending
    .map((l) => ({ orderLineId: l.id, quantity: Number(qty[l.id] || 0) }))
    .filter((x) => x.quantity > 0);
  const partial = picked.length < pending.length || pending.some((l) => Number(qty[l.id] || 0) < rest(l));
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${d.id}/shipments`,
        {
          carrier: f.carrier,
          trackingNumber: f.trackingNumber.trim(),
          trackingUrl: f.trackingUrl.trim() || null,
          packageCount: Number(f.packageCount) || 1,
          lines: picked,
        },
        { idempotencyKey: idem },
      ),
    {
      invalidate,
      success: partial ? t('admin.orders.shippedPartial') : t('admin.orders.shipped'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.shipTitle')}
      description={t('admin.orders.shipLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!picked.length}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.orders.ship')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 flex items-center gap-1 text-sm font-medium text-ink">
            {t('admin.orders.shipItems')} <HelpTip k="orders.ship_lines" />
          </legend>
          <ul className="divide-y divide-line rounded-md border border-line text-sm">
            {pending.map((l) => (
              <li key={l.id} className="grid grid-cols-[1fr_5.5rem] items-center gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate">{l.title}</p>
                  <p className="tabular text-ink-3">
                    {l.sku} ·{' '}
                    {t('admin.orders.shipProgress', { shipped: l.shippedQuantity, ordered: l.quantity })}
                  </p>
                </div>
                <Input
                  aria-label={t('admin.orders.shipQty', { title: l.title })}
                  inputMode="numeric"
                  className="h-9 text-end tabular"
                  disabled={cod}
                  value={qty[l.id] ?? ''}
                  onChange={(e) => {
                    const max = rest(l);
                    const v = e.target.value.replace(/\D/g, '');
                    setQty({ ...qty, [l.id]: v === '' ? '' : String(Math.min(Number(v), max)) });
                  }}
                />
              </li>
            ))}
          </ul>
          {cod ? (
            <p className="mt-2 text-sm text-ink-2">{t('admin.orders.shipCodNote')}</p>
          ) : !picked.length ? (
            <p role="alert" className="mt-2 text-sm text-rose">
              {t('admin.orders.shipNothing')}
            </p>
          ) : (
            partial && <p className="mt-2 text-sm text-amber">{t('admin.orders.shipPartialNote')}</p>
          )}
          {fieldError(m.error, 'lines') && (
            <p role="alert" className="mt-2 text-[0.8125rem] font-medium text-rose">
              {fieldError(m.error, 'lines')}
            </p>
          )}
        </fieldset>
        <Field label={t('admin.shipping.carrier')}>
          {(p) => (
            <Select {...p} value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })}>
              {CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.shipping.carriers.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.orders.trackingNumber')}
          required
          error={fieldError(m.error, 'trackingNumber')}
        >
          {(p) => (
            <Input
              {...p}
              className="tabular"
              value={f.trackingNumber}
              onChange={(e) => setF({ ...f, trackingNumber: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.orders.trackingUrl')}
          optionalLabel={t('common.optional')}
          className="sm:col-span-2"
          description={t('admin.orders.trackingUrlHint')}
          error={fieldError(m.error, 'trackingUrl')}
        >
          {(p) => (
            <Input
              {...p}
              type="url"
              placeholder="https://"
              value={f.trackingUrl}
              onChange={(e) => setF({ ...f, trackingUrl: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.orders.packageCount')}>
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              className="tabular"
              value={f.packageCount}
              onChange={(e) => setF({ ...f, packageCount: e.target.value.replace(/\D/g, '') })}
            />
          )}
        </Field>
      </div>
      {m.error && !m.error.errors.length && (
        <p role="alert" className="mt-3 text-sm text-rose">
          {m.error.title}
        </p>
      )}
    </Dialog>
  );
}

/**
 * Geliver ile kargola (F7-28): paketteki ürünler, koli ölçüleri ve ağırlık → Geliver'de gönderi ve kargo
 * firmalarının teklifleri → seçilen teklifle etiket satın alma (ücret onayla). Teklifler eşzamansız gelir; tamamlanmazsa
 * "Yenile" ile okunur.
 */
function GeliverShipDialog({
  order: d,
  info,
  invalidate,
  onClose,
}: {
  order: OrderDetail;
  info: GeliverOrderInfo;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const money = useMoney();
  const rest = (l: OrderDetail['lines'][number]) => l.quantity - l.shippedQuantity - l.cancelledQuantity;
  const pending = d.lines.filter((l) => rest(l) > 0);
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(pending.map((l) => [l.id, String(rest(l))])),
  );
  const picked = pending
    .map((l) => ({ orderLineId: l.id, quantity: Number(qty[l.id] || 0) }))
    .filter((x) => x.quantity > 0);
  const whole = picked.length === pending.length && pending.every((l) => Number(qty[l.id] || 0) === rest(l));
  const [f, setF] = useState({
    lengthCm: String(info.parcel.lengthCm),
    widthCm: String(info.parcel.widthCm),
    heightCm: String(info.parcel.heightCm),
    weightGrams: String(info.weightGrams || ''),
  });
  const [offers, setOffers] = useState<GeliverOffersView | null>(null);
  const [choice, setChoice] = useState('');
  const [confirming, setConfirming] = useState(false);
  const digits = (v: string) => v.replace(/\D/g, '');
  const lines = whole ? undefined : picked;
  const get = useApiMutation(
    () =>
      api.post<GeliverOffersView>(
        `/v1/admin/orders/${d.id}/geliver/offers`,
        {
          lines,
          lengthCm: Number(f.lengthCm),
          widthCm: Number(f.widthCm),
          heightCm: Number(f.heightCm),
          weightGrams: Number(f.weightGrams),
        },
        // Her tıklama yeni teklif ister (adres düzeltildikten sonra eski hata yinelenmesin).
        { idempotencyKey: newIdempotencyKey() },
      ),
    {
      silentFieldErrors: true,
      onSuccess: (r) => {
        setOffers(r);
        setChoice(r.offers[0]?.id ?? '');
        setConfirming(false);
      },
    },
  );
  const refresh = useApiMutation(
    () =>
      api.get<GeliverOffersView>(
        `/v1/admin/orders/${d.id}/geliver/offers/${encodeURIComponent(offers!.geliverShipmentId)}`,
      ),
    {
      onSuccess: (r) => {
        setOffers(r);
        if (!r.offers.some((o) => o.id === choice)) setChoice(r.offers[0]?.id ?? '');
      },
    },
  );
  const selected = offers?.offers.find((o) => o.id === choice) ?? null;
  const buy = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${d.id}/geliver/purchase`,
        {
          geliverShipmentId: offers!.geliverShipmentId,
          offerId: choice,
          lines,
          weightGrams: Number(f.weightGrams) || null,
        },
        { idempotencyKey: newIdempotencyKey() },
      ),
    { invalidate, success: t('admin.orders.geliver.bought'), onSuccess: onClose },
  );
  const sizeOk = [f.lengthCm, f.widthCm, f.heightCm].every((v) => Number(v) >= 1 && Number(v) <= 300);
  const weightOk = Number(f.weightGrams) >= 10 && Number(f.weightGrams) <= 100_000;
  const err = get.error ?? refresh.error ?? buy.error;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.geliver.ship')}
      description={t('admin.orders.geliver.lead')}
      closeLabel={t('common.close')}
      footer={
        confirming && selected ? (
          <>
            <Button onClick={() => setConfirming(false)}>{t('admin.orders.geliver.back')}</Button>
            <Button variant="primary" loading={buy.isPending} onClick={() => buy.mutate(undefined)}>
              {t('admin.orders.geliver.confirmYes')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <span className="flex items-center gap-1">
              <Button
                loading={get.isPending}
                disabled={!picked.length || !sizeOk || !weightOk}
                onClick={() => get.mutate(undefined)}
              >
                {t('admin.orders.geliver.getOffers')}
              </Button>
              <HelpTip k="orders.geliver_offers" />
            </span>
            <span className="flex items-center gap-1">
              <Button variant="primary" disabled={!selected} onClick={() => setConfirming(true)}>
                {selected
                  ? t('admin.orders.geliver.buy', { amount: money(selected.amountMinor, selected.currency) })
                  : t('admin.orders.geliver.buy', { amount: '—' })}
              </Button>
              <HelpTip k="orders.geliver_buy" />
            </span>
          </>
        )
      }
    >
      {confirming && selected ? (
        <p className="rounded-sm border border-amber/40 bg-amber-tint px-3 py-2 text-sm text-ink">
          {info.test
            ? t('admin.orders.geliver.confirmTest')
            : t('admin.orders.geliver.confirm', { amount: money(selected.amountMinor, selected.currency) })}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {info.test && (
            <p className="sm:col-span-2">
              <Badge tone="amber">{t('admin.orders.geliver.testBadge')}</Badge>
            </p>
          )}
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 flex items-center gap-1 text-sm font-medium text-ink">
              {t('admin.orders.shipItems')} <HelpTip k="orders.ship_lines" />
            </legend>
            <ul className="divide-y divide-line rounded-md border border-line text-sm">
              {pending.map((l) => (
                <li key={l.id} className="grid grid-cols-[1fr_5.5rem] items-center gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate">{l.title}</p>
                    <p className="tabular text-ink-3">
                      {l.sku} ·{' '}
                      {t('admin.orders.shipProgress', { shipped: l.shippedQuantity, ordered: l.quantity })}
                    </p>
                  </div>
                  <Input
                    aria-label={t('admin.orders.shipQty', { title: l.title })}
                    inputMode="numeric"
                    className="h-9 text-end tabular"
                    value={qty[l.id] ?? ''}
                    onChange={(e) => {
                      const v = digits(e.target.value);
                      setQty({ ...qty, [l.id]: v === '' ? '' : String(Math.min(Number(v), rest(l))) });
                      setOffers(null);
                    }}
                  />
                </li>
              ))}
            </ul>
          </fieldset>
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 flex items-center gap-1 text-sm font-medium text-ink">
              {t('admin.shipping.geliver.parcel')} <HelpTip k="orders.geliver_parcel" />
            </legend>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(['lengthCm', 'widthCm', 'heightCm'] as const).map((k) => (
                <Field key={k} label={t(`admin.shipping.geliver.${k.replace('Cm', '')}`)}>
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      className="tabular"
                      value={f[k]}
                      onChange={(e) => {
                        setF({ ...f, [k]: digits(e.target.value) });
                        setOffers(null);
                      }}
                    />
                  )}
                </Field>
              ))}
              <Field label={t('admin.orders.geliver.weight')}>
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    className="tabular"
                    value={f.weightGrams}
                    onChange={(e) => {
                      setF({ ...f, weightGrams: digits(e.target.value) });
                      setOffers(null);
                    }}
                  />
                )}
              </Field>
            </div>
            {info.weightMissingLines > 0 && (
              <p className="mt-2 text-sm text-amber">
                {t('admin.orders.geliver.weightMissing', { count: info.weightMissingLines })}
              </p>
            )}
          </fieldset>
          {offers && (
            <fieldset className="sm:col-span-2">
              <legend className="mb-2 flex items-center gap-1 text-sm font-medium text-ink">
                {t('admin.orders.geliver.offers')} <HelpTip k="orders.geliver_offer" />
              </legend>
              {!offers.complete && (
                <p className="mb-2 flex items-center gap-2 text-sm text-ink-2">
                  {t('admin.orders.geliver.offersPending', { percent: offers.percent })}
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={refresh.isPending}
                    onClick={() => refresh.mutate(undefined)}
                  >
                    {t('admin.orders.geliver.refresh')}
                  </Button>
                </p>
              )}
              {!offers.offers.length ? (
                <p className="text-sm text-ink-2">{t('admin.orders.geliver.noOffers')}</p>
              ) : (
                <ul className="divide-y divide-line rounded-md border border-line text-sm">
                  {offers.offers.map((o) => (
                    <li key={o.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
                        <input
                          type="radio"
                          name="geliver-offer"
                          value={o.id}
                          checked={choice === o.id}
                          onChange={() => setChoice(o.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{t(`admin.shipping.carriers.${o.carrier}`)}</span>
                          <span className="text-ink-3">
                            {' '}
                            · {o.serviceCode || o.providerCode}
                            {o.estimate ? ` · ${o.estimate}` : ''}
                          </span>
                          {o.id === offers.cheapestId && (
                            <>
                              {' '}
                              <Badge tone="moss">{t('admin.orders.geliver.cheapest')}</Badge>
                            </>
                          )}
                          {o.id === offers.fastestId && (
                            <>
                              {' '}
                              <Badge tone="slate">{t('admin.orders.geliver.fastest')}</Badge>
                            </>
                          )}
                        </span>
                        <span className="tabular font-medium">{money(o.amountMinor, o.currency)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          )}
        </div>
      )}
      {err && (
        <p role="alert" className="mt-3 text-sm text-rose">
          {err.title}
        </p>
      )}
    </Dialog>
  );
}

/**
 * Kısmi iptal (F5B-27): bölünmüş siparişte gönderilemeyecek ürünler iptal edilir. Ayrılan stok bırakılır; para
 * iadesi Ödeme bölümünden "İade et" ile ayrıca yapılır (tutar önerilir).
 */
function CancelItemsDialog({ order: d, onClose }: { order: OrderDetail; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const rest = (l: OrderDetail['lines'][number]) => l.quantity - l.shippedQuantity - l.cancelledQuantity;
  const pending = d.lines.filter((l) => rest(l) > 0);
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(pending.map((l) => [l.id, String(rest(l))])),
  );
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const picked = pending
    .map((l) => ({ orderLineId: l.id, quantity: Number(qty[l.id] || 0) }))
    .filter((x) => x.quantity > 0);
  const m = useApiMutation(
    () =>
      api.post<{ cancelledMinor: number; currency: string }>(
        `/v1/admin/orders/${d.id}/items/cancel`,
        { lines: picked, note: note.trim() },
        { idempotencyKey: idem, ifMatch: d.version },
      ),
    {
      invalidate: [['order', d.id], ['orders']],
      success: (r) => t('admin.orders.itemsCancelled', { amount: money(r.cancelledMinor, r.currency) }),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.cancelItems')}
      description={t('admin.orders.cancelItemsLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={m.isPending}
            disabled={!picked.length || note.trim().length < 3}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.orders.cancelItems')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="divide-y divide-line rounded-md border border-line text-sm">
          {pending.map((l) => (
            <li key={l.id} className="grid grid-cols-[1fr_5.5rem] items-center gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate">{l.title}</p>
                <p className="tabular text-ink-3">
                  {l.sku} ·{' '}
                  {t('admin.orders.shipProgress', { shipped: l.shippedQuantity, ordered: l.quantity })}
                </p>
              </div>
              <Input
                aria-label={t('admin.orders.cancelQty', { title: l.title })}
                inputMode="numeric"
                className="h-9 text-end tabular"
                value={qty[l.id] ?? ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setQty({ ...qty, [l.id]: v === '' ? '' : String(Math.min(Number(v), rest(l))) });
                }}
              />
            </li>
          ))}
        </ul>
        <Field
          label={t('admin.orders.cancelItemsNote')}
          required
          description={t('admin.orders.cancelItemsNoteHint')}
          error={fieldError(m.error, 'note')}
        >
          {(p) => (
            <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>
        {(fieldError(m.error, 'lines') || (m.error && !m.error.errors.length)) && (
          <p role="alert" className="text-sm text-rose">
            {fieldError(m.error, 'lines') ?? m.error?.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ShipmentStatusDialog({
  shipmentId,
  invalidate,
  onClose,
}: {
  shipmentId: string;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const [status, setStatus] = useState<(typeof SHIPMENT_STATUSES)[number]>('delivered');
  const [description, setDescription] = useState('');
  const m = useApiMutation(
    () =>
      api.post(`/v1/admin/shipments/${shipmentId}/status`, {
        status,
        description: description.trim() || null,
      }),
    { invalidate, success: t('common.saved'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.updateShipment')}
      description={t('admin.orders.updateShipmentLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.orders.shipmentStatus')}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              {SHIPMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.orders.shipmentStatuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {status === 'cancelled' && (
          <p className="rounded-sm border border-amber/40 bg-amber-tint px-3 py-2 text-sm text-ink">
            {t('admin.orders.shipmentCancelNote')}
          </p>
        )}
        <Field label={t('admin.orders.shipmentNote')} optionalLabel={t('common.optional')}>
          {(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        {m.error && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function InvoicesSection({ order: d }: { order: OrderDetail }) {
  const t = useT();
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const active = d.invoices.some((i) => i.status !== 'cancelled' && i.status !== 'failed');
  const allowed = !['draft', 'cancelled'].includes(d.status);
  const inv = [['order', d.id], ['invoices']];
  if (!d.invoices.length && !allowed) return null;
  return (
    <Section
      title={t('admin.orders.invoicesTitle')}
      action={
        can('invoices.invoice.write') &&
        allowed &&
        !active && (
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => setOpen(true)}>
              {t('admin.orders.createInvoice')}
            </Button>
            <HelpTip k="invoices.create" />
          </div>
        )
      }
    >
      {!d.invoices.length ? (
        <p className="text-sm text-ink-2">{t('admin.orders.noInvoices')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
          {d.invoices.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {t(`admin.invoices.providers.${i.provider}`)}
                  {i.number && <span className="font-normal text-ink-2"> · {i.number}</span>}
                </p>
                {i.provider === 'bizimhesap' && i.customerCode && (
                  <p className="text-ink-2">
                    {t('admin.invoices.bhTarget', {
                      code: i.customerCode,
                      cash: i.cashId ?? t('admin.invoices.bhNoCash'),
                    })}
                  </p>
                )}
                {i.error && <p className="text-ink-3">{i.error}</p>}
              </div>
              <Badge tone={invoiceTone[i.status]}>{t(`admin.invoices.statuses.${i.status}`)}</Badge>
              {i.providerUrl && (
                <a
                  href={i.providerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pine hover:underline"
                >
                  {t('admin.invoices.open')}
                </a>
              )}
              <InvoiceActions invoice={i} invalidate={inv} />
            </li>
          ))}
        </ul>
      )}
      {open && <CreateInvoiceDialog orderId={d.id} invalidate={inv} onClose={() => setOpen(false)} />}
    </Section>
  );
}

function CreateInvoiceDialog({
  orderId,
  invalidate,
  onClose,
}: {
  orderId: string;
  invalidate: Array<Array<string>>;
  onClose: () => void;
}) {
  const t = useT();
  const [provider, setProvider] = useState<'bizimhesap' | 'manual'>('bizimhesap');
  const [number, setNumber] = useState('');
  const [kind, setKind] = useState<'e_arsiv' | 'e_fatura' | 'unknown'>('e_arsiv');
  const [idem] = useState(() => new IdempotencyKey());
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/orders/${orderId}/invoices`,
        { provider, kind, ...(provider === 'manual' ? { number: number.trim() } : {}) },
        { idempotencyKey: idem },
      ),
    { invalidate, success: t('common.saved'), onSuccess: onClose, silentFieldErrors: true },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.orders.createInvoice')}
      description={
        provider === 'bizimhesap' ? t('admin.invoices.bizimhesapLead') : t('admin.invoices.manualLead')
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate(undefined)}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.invoices.provider')}>
          {(p) => (
            <Select {...p} value={provider} onChange={(e) => setProvider(e.target.value as typeof provider)}>
              <option value="bizimhesap">{t('admin.invoices.providers.bizimhesap')}</option>
              <option value="manual">{t('admin.invoices.providers.manual')}</option>
            </Select>
          )}
        </Field>
        <Field label={t('admin.invoices.kind')}>
          {(p) => (
            <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              {(['e_arsiv', 'e_fatura', 'unknown'] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`admin.invoices.kinds.${k}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {provider === 'manual' && (
          <Field label={t('admin.invoices.number')} required error={fieldError(m.error, 'number')}>
            {(p) => <Input {...p} value={number} onChange={(e) => setNumber(e.target.value)} />}
          </Field>
        )}
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function ContractsSection({ order: d }: { order: OrderDetail }) {
  const t = useT();
  const { formatDate } = useI18n();
  const [view, setView] = useState<OrderDetail['acceptances'][number] | null>(null);
  return (
    <Section title={t('admin.orders.contractsTitle')} description={t('admin.orders.contractsLead')}>
      <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
        {d.acceptances.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
            <span className="flex-1">
              {t(`admin.legal.kinds.${a.kind}`)} · {t('admin.legal.versionLabel', { version: a.version })}
              {/* Kabul edilen dil sürümü (F7-26). */}
              {a.locale && ` · ${LOCALE_NAMES[a.locale as keyof typeof LOCALE_NAMES] ?? a.locale}`}
            </span>
            <span className="text-ink-3">{formatDate(a.acceptedAt, true)}</span>
            {a.renderedText ? (
              <Button size="sm" variant="ghost" onClick={() => setView(a)}>
                {t('admin.legal.view')}
              </Button>
            ) : (
              <span className="text-ink-3">{t('admin.customers.masked')}</span>
            )}
          </li>
        ))}
      </ul>
      {view && (
        <Dialog
          open
          onOpenChange={(v) => !v && setView(null)}
          variant="sheet"
          title={t(`admin.legal.kinds.${view.kind}`)}
          description={t('admin.orders.contractHash', { hash: (view.renderedHash ?? '').slice(0, 16) })}
          closeLabel={t('common.close')}
        >
          <pre className="whitespace-pre-wrap break-words font-sans text-sm">{view.renderedText}</pre>
        </Dialog>
      )}
    </Section>
  );
}

/**
 * Siparişin kur bilgisi (F7-11): sipariş anındaki TCMB döviz alış kuru (sonradan değişmez), TL karşılığı (bilgi) ve
 * satırların fiyatını veren kurdan türetilmiş listelerin kuru. Kur yoksa uydurulmaz.
 */
function OrderFxNote({ fx, totalMinor }: { fx: NonNullable<OrderDetail['fx']>; totalMinor: number }) {
  const t = useT();
  const money = useMoney();
  const pair = usePairLabel();
  const day = useBulletinDate();
  const tl =
    fx.rate && fx.unit
      ? convertCross(
          moneyOf(totalMinor, fx.currency),
          { rate: fx.rate, unit: fx.unit },
          {
            currency: 'TRY',
            rate: '1',
            unit: 1,
          },
        ).amountMinor
      : null;
  return (
    <div className="mt-4 space-y-1 border-t border-line pt-3 text-sm text-ink-2" data-testid="order-fx">
      <p className="inline-flex items-center gap-1 font-medium text-ink">
        {t('admin.fx.orderRate')} <HelpTip k="orders.fx" />
      </p>
      <p>
        {fx.rate && fx.unit && fx.bulletinDate
          ? t('admin.fx.orderRateLine', {
              pair: pair({ currency: fx.currency, rate: fx.rate, unit: fx.unit }),
              date: day(fx.bulletinDate),
              no: fx.bulletinNo ?? '—',
            })
          : t('admin.fx.orderRateNone')}
      </p>
      {tl !== null && <p>{t('admin.fx.orderTryEquivalent', { amount: money(tl, 'TRY') })}</p>}
      {fx.stale && <p className="text-amber">{t('admin.fx.orderRateStale')}</p>}
      {fx.priceLists.map((l) => (
        <p key={l.id}>
          {t('admin.fx.orderListRate', {
            name: l.name,
            pair: [
              ...(l.fromCurrency !== 'TRY'
                ? [pair({ currency: l.fromCurrency, rate: l.fromRate, unit: l.fromUnit })]
                : []),
              pair({ currency: fx.currency, rate: l.toRate, unit: l.toUnit }),
            ].join(' · '),
            rateType: t(`admin.fx.rateTypes.${l.rateType}`),
            date: day(l.bulletinDate),
          })}
        </p>
      ))}
    </div>
  );
}
